import { useQuery } from '@tanstack/react-query';

import { readWorkspaceUsage } from '@/lib/api/generated/api';
import { UsageError, isForbidden } from '../errors';
import {
  profileLine,
  summaryLines,
  type SplitDimension,
  type SplitRead,
} from '../dailySplit';
import { usageKeys } from '../queryKeys';
import { useWorkspaceSpendGroups } from './useWorkspaceSpendGroups';

/**
 * Each day's parts for the dimension the Per day bars split by (spec 257, service
 * spec 207), and the period's lines they join by key.
 *
 * Workflow, person and model come with the usage summary when it is asked for them
 * (`split=`); profile comes from the AI models spend read, which already carries the
 * period's profile lines and is read for Where it went, so asking costs nothing more.
 * Only the split shown is asked for, and only while the Per day view is open.
 */
export function useDailySplit(
  workspaceId: string,
  period: string | undefined,
  dimension: SplitDimension,
  enabled: boolean,
): { data: SplitRead | undefined; isPending: boolean; isError: boolean } {
  const fromSummary = dimension !== 'profile';
  const summary = useQuery({
    enabled: enabled && fromSummary,
    queryKey: [...usageKeys.workspace(workspaceId, period), 'split', dimension],
    queryFn: async ({ signal }) => {
      const response = await readWorkspaceUsage(
        workspaceId,
        {
          ...(period === undefined ? {} : { period }),
          split: dimension === 'profile' ? undefined : dimension,
        },
        { signal },
      );
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only this workspace’s owner can see what it is spending.',
        );
      }
      throw new UsageError(
        'unavailable',
        'Could not read this workspace’s spend by day.',
      );
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  });
  const groups = useWorkspaceSpendGroups(workspaceId, period);

  if (!fromSummary) {
    return {
      data: groups.data
        ? {
            lines: groups.data.by_profile.map(profileLine),
            days: groups.data.by_day,
          }
        : undefined,
      isPending: groups.isPending,
      isError: groups.isError,
    };
  }
  const read = summary.data;
  const lines =
    read === undefined
      ? undefined
      : dimension === 'workflow'
        ? summaryLines('workflow', read.by_workflow)
        : dimension === 'person'
          ? summaryLines('person', read.by_person)
          : summaryLines('model', read.by_model);
  return {
    data:
      read === undefined || lines === undefined
        ? undefined
        : {
            lines,
            days: read.by_day.map((day) => ({
              date: day.date,
              parts: day.parts ?? [],
            })),
          },
    isPending: enabled && summary.isPending,
    isError: summary.isError,
  };
}
